import React, { useEffect, useState, useRef } from 'react';
import '../styles/components/CustomCursor.css';

const CustomCursor = () => {
  const [cursorType, setCursorType] = useState('default');
  const [isClicking, setIsClicking] = useState(false);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const mousePos = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const cursorPos = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const dotPos = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const animationFrameId = useRef(null);

  useEffect(() => {
    // Fast position updates using RAF
    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
    };

    // Smooth animation loop using LERP and CSS Variables (Hardware Accelerated)
    const animate = () => {
      if (cursorRef.current && cursorDotRef.current) {
        const { x, y } = mousePos.current;
        
        // Dot moves instantly
        dotPos.current.x = x;
        dotPos.current.y = y;
        
        // Outer cursor uses Linear Interpolation (LERP) for a smooth trailing effect
        cursorPos.current.x += (x - cursorPos.current.x) * 0.25;
        cursorPos.current.y += (y - cursorPos.current.y) * 0.25;

        cursorRef.current.style.setProperty('--x', `${cursorPos.current.x}px`);
        cursorRef.current.style.setProperty('--y', `${cursorPos.current.y}px`);
        
        cursorDotRef.current.style.setProperty('--x', `${dotPos.current.x}px`);
        cursorDotRef.current.style.setProperty('--y', `${dotPos.current.y}px`);
      }
      animationFrameId.current = requestAnimationFrame(animate);
    };

    // Element detection - check parent tree
    const detectElement = (element) => {
      if (!element || element === document.body) return 'default';

      // Check tag names first (fastest)
      const tagName = element.tagName?.toLowerCase();
      
      if (tagName === 'button') return 'button';
      if (tagName === 'a') return 'link';
      if (tagName === 'input' || tagName === 'textarea') return 'text';
      if (tagName === 'select') return 'dropdown';

      // Check classes
      const className = element.className || '';
      if (typeof className === 'string') {
        // Buttons
        if (className.includes('btn-hero-explore') || 
            className.includes('see-more-btn') ||
            className.includes('newsletter-btn') ||
            className.includes('event-btn') ||
            className.includes('tool-action-btn') ||
            className.includes('join-academy-btn') ||
            className.includes('main-post-btn')) {
          return 'button';
        }

        // Text inputs
        if (className.includes('newsletter-input') ||
            className.includes('whats-next-search-input') ||
            className.includes('blogpage-search-input')) {
          return 'text';
        }

        // Dropdowns/filters
        if (className.includes('whats-next-filter-btn') ||
            className.includes('form-select')) {
          return 'dropdown';
        }

        // Cards
        if (className.includes('article-card') ||
            className.includes('event-card') ||
            className.includes('tool-card') ||
            className.includes('team-card') ||
            className.includes('academy-program-block') ||
            className.includes('experiment-card')) {
          return 'card';
        }

        // Tabs
        if (className.includes('tab-btn')) {
          return 'tab';
        }

        // Video
        if (className.includes('video-mute-toggle')) {
          return 'video';
        }

        // Social
        if (className.includes('social-link') || 
            className.includes('linkedin-icon')) {
          return 'social';
        }

        // Navigation
        if (className.includes('menu-item')) {
          return 'link';
        }
      }

      // Check parent recursively
      return detectElement(element.parentElement);
    };

    // Hover detection with light throttle
    let lastType = 'default';
    let throttleTimer = null;
    
    const handleHover = (e) => {
      if (throttleTimer) return;
      
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
      }, 50);

      const newType = detectElement(e.target);
      
      if (newType !== lastType) {
        lastType = newType;
        setCursorType(newType);
      }
    };

    // Click handlers
    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);

    // Add listeners
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseover', handleHover, { passive: true });
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    // Start animation
    animate();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleHover);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, []);

  // Don't show on mobile
  if (typeof window !== 'undefined' && window.innerWidth < 992) {
    return null;
  }

  return (
    <>
      <div
        ref={cursorRef}
        className={`custom-cursor custom-cursor-${cursorType} ${isClicking ? 'clicking' : ''}`}
      >
        {cursorType === 'button' && (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {cursorType === 'text' && (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 2v16M6 6h8M6 14h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
        {cursorType === 'dropdown' && (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {cursorType === 'video' && (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 5l7 5-7 5V5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {cursorType === 'social' && (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2"/>
            <path d="M10 6v8M6 10h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
      </div>

      <div
        ref={cursorDotRef}
        className={`custom-cursor-dot custom-cursor-dot-${cursorType} ${isClicking ? 'clicking' : ''}`}
      />
    </>
  );
};

export default CustomCursor;